import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output,
  ViewEncapsulation, inject,
} from '@angular/core';
import { TcBackButtonComponent } from '../tc-back-button/tc-back-button.component';
import { TcAccountChipComponent } from '../tc-account-chip/tc-account-chip.component';

/**
 * TC-A1 — the single global sticky header for every browse surface. A
 * `44px | 1fr | 44px` glass bar: back button (or spacer) · title/subtitle ·
 * right slot. The right slot projects a screen-specific widget (menu search,
 * receipt print) and otherwise falls back to the account chip. Mounted once in
 * AppComponent above the router-outlet; flow/detail screens opt out via route
 * data so it never duplicates their free-floating back button.
 *
 * Standardises the inconsistencies the per-screen `.tc-head` blocks had
 * (webkit blur prefix, border, spacer fallback). Logical CSS props for RTL.
 */
@Component({
  selector: 'tc-app-header',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [TcBackButtonComponent, TcAccountChipComponent],
  template: `
    <header class="tc-app-header">
      @if (showBack) {
        <tc-back-button [ariaLabel]="backAriaLabel" (back)="back.emit()" />
      } @else {
        <span class="tc-app-header__spacer" aria-hidden="true"></span>
      }

      <div class="tc-app-header__center">
        @if (title) { <div class="tc-app-header__title">{{ title }}</div> }
        @if (subtitle) { <div class="tc-app-header__sub">{{ subtitle }}</div> }
      </div>

      <div class="tc-app-header__right">
        <ng-content select="[headerRight]"></ng-content>
        @if (showChip) { <tc-account-chip [publicToken]="publicToken" /> }
      </div>
    </header>
  `,
  styles: [`
    .tc-app-header {
      position: fixed; inset-block-start: 0; z-index: 20;
      display: grid; grid-template-columns: 44px 1fr 44px;
      align-items: center; gap: 8px; padding: 12px;
      background: var(--tc-chrome-header-bg);
      backdrop-filter: saturate(120%) blur(8px);
      -webkit-backdrop-filter: saturate(120%) blur(8px);
      border-block-end: 1px solid var(--tc-border);width: 100%;
    max-width: 480px;top: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    }
    .tc-app-header__spacer { inline-size: 44px; block-size: 44px; visibility: hidden; }
    .tc-app-header__center { text-align: center; line-height: 1.1; min-inline-size: 0; }
    .tc-app-header__title {
      font-weight: 700; font-size: 16px; color: var(--tc-fg);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .tc-app-header__sub {
      font-size: 12px; color: var(--tc-muted); margin-block-start: 2px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .tc-app-header__right {
      display: inline-flex; align-items: center; justify-content: flex-end;
      min-inline-size: 44px; gap: 8px;
    }
    @media print { .tc-app-header { display: none !important; } }
  `],
})
export class TcAppHeaderComponent implements AfterViewInit, OnDestroy {
  /** Show the back button (left); false renders a balancing spacer (landing). */
  @Input() showBack = true;
  @Input() backAriaLabel = 'Back';
  @Input() title: string | null = null;
  @Input() subtitle: string | null = null;
  /** Forwarded to the default account chip; chip hides when null. */
  @Input() publicToken: string | null = null;
  /** Set false when a screen projects its own right widget via [headerRight]. */
  @Input() showChip = true;

  /** Emitted from the back button so the host runs its own destination-aware nav. */
  @Output() back = new EventEmitter<void>();

  private hostEl = inject(ElementRef<HTMLElement>);
  private ro?: ResizeObserver;

  // Publish the header's live height as --tc-app-header-h so screens with their
  // own sticky sub-chrome (e.g. the menu's category rail) can pin BELOW the
  // header and offset their scroll-spy. ResizeObserver also catches the
  // one-line ↔ two-line change when a subtitle appears.
  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined' || typeof document === 'undefined') return;
    // Measure the actual `.tc-app-header` bar, NOT the component host. The bar
    // is `position: fixed` (out of flow), so it contributes ZERO height to the
    // host element — observing the host publishes --tc-app-header-h ≈ 0, which
    // makes every sticky sub-chrome (the menu's category + search rail) pin at
    // viewport top and hide BEHIND this fixed header. Fall back to the host
    // only if the bar can't be found.
    const bar = this.hostEl.nativeElement.querySelector('.tc-app-header') as HTMLElement | null;
    const measured: HTMLElement = bar ?? this.hostEl.nativeElement;
    this.ro = new ResizeObserver(() => {
      const h = Math.round(measured.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--tc-app-header-h', `${h}px`);
    });
    this.ro.observe(measured);
  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
    // Header gone (flow screen) → zero the offset so non-header screens are unaffected.
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--tc-app-header-h', '0px');
    }
  }
}
